import { Link } from "react-router-dom";
import TrustLayout, { Section, P, UL } from "../components/TrustLayout";

const versionHistory = [
  { version: "2.0", date: "2 April 2026", notes: "Complete revision — restructured into 11 numbered sections; aligned with TrustLayout and Trust Center." },
  { version: "1.0", date: "25 November 2025", notes: "Initial publication of the Technofocus Cookie Policy." },
];

const cookieSections = [
  { id: "ck-intro", label: "1. Introduction" },
  { id: "ck-what", label: "2. What Are Cookies?" },
  { id: "ck-use", label: "3. How We Use Cookies" },
  { id: "ck-categories", label: "4. Categories of Cookies" },
  { id: "ck-third-party", label: "5. Third-Party Cookies" },
  { id: "ck-consent", label: "6. Consent & Management" },
  { id: "ck-control", label: "7. How to Control Cookies" },
  { id: "ck-retention", label: "8. Cookie Retention" },
  { id: "ck-transfers", label: "9. International Data Transfers" },
  { id: "ck-updates", label: "10. Updates to This Policy" },
  { id: "ck-contact", label: "11. Contact Us" },
];

const cookieCategories = [
  {
    id: "strictly-necessary",
    badge: "Strictly Necessary",
    badgeColor: "#194449",
    desc: "These cookies are essential for the operation of our Services and cannot be disabled. They are used to:",
    items: ["Enable login and authentication", "Maintain session state", "Ensure platform security"],
  },
  {
    id: "functional",
    badge: "Functional",
    badgeColor: "#2E7D7B",
    desc: "These cookies allow the platform to remember choices you make, such as:",
    items: ["Language preferences", "User settings", "Interface customization"],
  },
  {
    id: "analytics",
    badge: "Analytics",
    badgeColor: "#3D8A88",
    desc: "These cookies help us understand how users interact with our Services by collecting information such as:",
    items: ["Pages visited", "Time spent on pages", "Navigation patterns"],
    note: "This information is used to improve platform performance and user experience.",
  },
  {
    id: "marketing",
    badge: "Marketing / Tracking",
    badgeColor: "#5A6472",
    desc: "In limited cases, cookies may be used to:",
    items: ["Measure the effectiveness of outreach or campaigns", "Understand engagement with content"],
    note: "Where required, such cookies are used only with user consent.",
    optional: true,
  },
];

export default function CookiePolicy() {
  return (
    <TrustLayout
      title="Cookie Policy"
      lastUpdated="2 April 2026"
      versionHistory={versionHistory}
      sections={cookieSections}
      testId="cookie-policy-page"
    >

      {/* 1. Introduction */}
      <Section id="ck-intro" title="Introduction" numbered="1">
        <P>
          This Cookie Policy explains how <strong>Technofocus Pte. Ltd.</strong> ("Technofocus", "we", "our", or "us") uses cookies and similar technologies when you access or use our platforms, including:
        </P>
        <UL items={[
          "LevelUp Learning Platform",
          "Why Microsoft – Compete Enablement for Partners",
          "Skilling-in-a-Box",
          "Associated websites and services",
        ]} />
        <P>
          This policy should be read together with our{" "}
          <Link to="/privacy" className="underline font-medium" style={{ color: '#194449' }}>
            Privacy Policy
          </Link>.
        </P>
      </Section>

      {/* 2. What Are Cookies */}
      <Section id="ck-what" title="What Are Cookies?" numbered="2">
        <P>Cookies are small text files that are placed on your device when you visit a website. They are widely used to:</P>
        <UL items={[
          "Enable website functionality",
          "Improve user experience",
          "Analyze usage and performance",
          "Support security and access management",
        ]} />
        <P>Cookies may be set by Technofocus (first-party cookies) or by third-party service providers (third-party cookies).</P>
      </Section>

      {/* 3. How We Use */}
      <Section id="ck-use" title="How We Use Cookies" numbered="3">
        <P>We use cookies and similar technologies for the following purposes:</P>
        <UL items={[
          "To enable essential platform functionality and authentication",
          "To remember user preferences and settings",
          "To analyze how users interact with our Services",
          "To improve platform performance and usability",
        ]} />
        <div className="mt-4 rounded-xl border px-5 py-4 flex items-start gap-3" style={{ borderColor: '#D4EC8E', backgroundColor: '#F9FFED' }}>
          <span className="text-sm font-semibold shrink-0" style={{ color: '#07641D' }}>Note:</span>
          <p className="text-sm text-gray-700">We do <strong>not</strong> use cookies to collect sensitive personal data.</p>
        </div>
      </Section>

      {/* 4. Categories */}
      <Section id="ck-categories" title="Categories of Cookies We Use" numbered="4">
        <div className="space-y-4 mt-2">
          {cookieCategories.map((cat, i) => (
            <div
              key={cat.id}
              id={`ck-cat-${cat.id}`}
              className="rounded-xl border overflow-hidden"
              style={{ borderColor: '#E2EBE9' }}
            >
              <div className="flex items-center gap-3 px-5 py-3.5" style={{ backgroundColor: '#F8FFFE', borderBottom: '1px solid #E2EBE9' }}>
                <span className="text-xs font-bold px-3 py-1 rounded-full text-white" style={{ backgroundColor: cat.badgeColor }}>
                  4.{i + 1}
                </span>
                <span className="font-semibold text-gray-900 text-sm" style={{ fontFamily: 'Outfit, sans-serif' }}>{cat.badge} Cookies</span>
                {cat.optional && (
                  <span className="ml-auto text-xs text-gray-400 italic">consent required</span>
                )}
              </div>
              <div className="px-5 py-4">
                <p className="text-sm text-gray-600 mb-3">{cat.desc}</p>
                <UL items={cat.items} />
                {cat.note && (
                  <p className="text-xs text-gray-500 italic mt-2">{cat.note}</p>
                )}
              </div>
            </div>
          ))}
        </div>
      </Section>

      {/* 5. Third-party */}
      <Section id="ck-third-party" title="Third-Party Cookies" numbered="5">
        <P>Some cookies may be placed by third-party service providers that support our Services, including:</P>
        <UL items={[
          "Analytics and performance tools",
          "Learning and content delivery platforms",
          "Event and engagement platforms",
          "Consent management tools (e.g., CookieYes)",
        ]} />
        <P>These providers may process data in accordance with their own privacy policies.</P>
      </Section>

      {/* 6. Consent */}
      <Section id="ck-consent" title="Cookie Consent and Management" numbered="6">
        <P>We use a consent management platform (<strong>CookieYes</strong>) to manage cookie preferences. When you first visit our Services:</P>
        <UL items={[
          "You will be presented with a cookie banner",
          "You can accept or reject non-essential cookies",
          "You can customize your preferences",
        ]} />
        <P>You can update your preferences at any time through the cookie settings link available on our website.</P>
      </Section>

      {/* 7. Control */}
      <Section id="ck-control" title="How to Control Cookies" numbered="7">
        <P>You can control or disable cookies through:</P>
        <UL items={[
          "Our cookie consent tool",
          "Your browser settings",
        ]} />
        <div className="mt-4 rounded-xl border px-5 py-4" style={{ borderColor: '#FBD8C0', backgroundColor: '#FFF7F3' }}>
          <p className="text-xs font-semibold mb-1.5" style={{ color: '#9A4A1A' }}>Please note:</p>
          <UL items={[
            "Disabling certain cookies may impact the functionality of our Services",
            "Some features may not work as intended without necessary cookies",
          ]} />
        </div>
      </Section>

      {/* 8. Retention */}
      <Section id="ck-retention" title="Cookie Retention" numbered="8">
        <P>Cookies may be stored on your device for varying durations:</P>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          {[
            { type: "Session Cookies", desc: "Deleted when you close your browser" },
            { type: "Persistent Cookies", desc: "Remain until they expire or are deleted" },
          ].map(c => (
            <div key={c.type} className="rounded-xl border px-5 py-4" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
              <p className="text-sm font-semibold text-gray-900 mb-1">{c.type}</p>
              <p className="text-xs text-gray-500">{c.desc}</p>
            </div>
          ))}
        </div>
        <P style={{ marginTop: '1rem' }}>The retention period depends on the purpose of the cookie.</P>
      </Section>

      {/* 9. Transfers */}
      <Section id="ck-transfers" title="International Data Transfers" numbered="9">
        <P>Cookies and related technologies may involve processing of data outside your jurisdiction, including in:</P>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 my-4">
          {["United States", "Singapore", "India", "United Kingdom"].map(loc => (
            <div key={loc} className="rounded-xl border px-4 py-3 text-center" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
              <p className="text-xs font-semibold text-gray-800">{loc}</p>
            </div>
          ))}
        </div>
        <P>
          Such processing is subject to appropriate safeguards as described in our{" "}
          <Link to="/privacy" className="underline font-medium" style={{ color: '#194449' }}>Privacy Policy</Link>.
        </P>
      </Section>

      {/* 10. Updates */}
      <Section id="ck-updates" title="Updates to This Policy" numbered="10">
        <P>We may update this Cookie Policy from time to time to reflect changes in our use of cookies or applicable regulations. Updated versions will be published on this page with a revised "Last Updated" date.</P>
      </Section>

      {/* 11. Contact */}
      <Section id="ck-contact" title="Contact Us" numbered="11">
        <P>For questions about this Cookie Policy or our use of cookies, please contact:</P>
        <div className="rounded-xl border p-5 inline-block mt-1" style={{ borderColor: '#E2EBE9', backgroundColor: '#F8FFFE' }}>
          <p className="text-sm font-semibold text-gray-900 mb-1">Data Protection</p>
          <a href="mailto:privacy@technofocus.ai" className="text-sm font-medium hover:underline" style={{ color: '#194449' }}>
            privacy@technofocus.ai
          </a>
        </div>
      </Section>

    </TrustLayout>
  );
}
